import React from "react";
import ReactQuill, { Quill } from "react-quill";
import cloneDeep from "lodash/cloneDeep";

const DEBUG_EDITOR = false;

//BEGIN allow image alignment styles
const ImageFormatAttributesList = ["alt", "height", "width", "style"];
var BaseImageFormat = Quill.import("formats/image");
class ImageFormat extends BaseImageFormat {
  static create(value) {
    const node = super.create(value);
    if (typeof value === "string") {
      node.setAttribute("src", this.sanitize(value));
    }
    return node;
  }

  static formats(domNode) {
    return ImageFormatAttributesList.reduce(function (formats, attribute) {
      if (domNode.hasAttribute(attribute)) {
        formats[attribute] = domNode.getAttribute(attribute);
      }
      return formats;
    }, {});
  }

  format(name, value) {
    if (ImageFormatAttributesList.indexOf(name) > -1) {
      if (value) {
        this.domNode.setAttribute(name, value);
      } else {
        this.domNode.removeAttribute(name);
      }
    } else {
      super.format(name, value);
    }
  }
}
Quill.register(ImageFormat, true);
//END allow image alignment styles

const ImageResize =
  require("imports-loader?window.Quill=quill!quill-image-resize-module").default;
Quill.register("modules/imageResize", ImageResize);

const STYLES = {
  quillTip: {
    fontSize: 10,
    lineHeight: "13px",
    marginTop: 5
  }
};

const STANDARD_FORMATS = [
  "background",
  "bold",
  "color",
  "code",
  "italic",
  "link",
  "size",
  "strike",
  "script",
  "underline",
  "blockquote",
  "header",
  "indent",
  "list",
  "bullet",
  "align",
  "direction",
  "code-block",
  "formula",
  "image",
  "video",
  "style",
  "width"
];

const STANDARD_MODULES = {
  toolbar: [
    [{ size: [] }, { align: [] }],
    [
      "bold",
      "italic",
      "strike",
      "underline",
      { color: [] },
      { background: [] },
      "link"
    ],
    [
      { list: "ordered" },
      { list: "bullet" },
      { indent: "-1" },
      { indent: "+1" }
    ],
    ["image"],
    ["clean"]
  ],
  clipboard: {
    matchVisual: false
  },
  imageResize: {}
};

const BASIC_FORMATS = [
  "bold",
  "italic",
  "strike",
  "underline",
  "link",
  "image",
  "video",
  "style",
  "width"
];

const BASIC_MODULES = {
  toolbar: [["bold", "italic", "strike", "underline", "link", "clean"]],
  clipboard: {
    matchVisual: false
  },
  imageResize: {}
};

class QuillEditor extends React.Component {
  constructor(props) {
    super(props);

    // The Quill Editor sometimes auto-modifies its `value` when it first receives it.
    // This can be problematic, as for example it modifies style attribute order and adds
    // additional props to some tags that our XSS filter later removes, which then means that
    // anytime someone loads the page, the Save Changes button will be enabled and the editor
    // will be marked as dirty, even though the person never edited anything. So we wait a bit
    // whenever the value changes before accepting QuillEditor onChange events.
    // Ref: https://eventiveteam.slack.com/archives/C018EML35BR/p1629243984102100
    this.hasLoaded = false;

    this.onChange = this.onChange.bind(this);
  }

  onChange(value, delta, source, editor) {
    DEBUG_EDITOR && console.log("QuillEditor: onChange", "source=", source);
    if (!this.hasLoaded) {
      DEBUG_EDITOR && console.log(" * skipping onChange bc hasLoaded=false");
      return;
    }

    let html = editor.getHTML();

    // Sanitize for diffing
    html = html.replace(/style=""\s*/g, "");

    // If the editor is empty, save just an empty string to the database,
    // not the <p><br></p> stub that is generated by quill
    // Santize empty input if santizeEmpty is set
    if (this.props.sanitizeEmpty && editor.getLength() === 1) {
      this.lastValue = html;
      this.props.onChange("");
    }

    DEBUG_EDITOR &&
      console.log("QuillEditor:  * firing onChange bc hasLoaded=true");
    this.lastValue = html;
    this.props.onChange(html);
  }

  _delayMarkLoaded() {
    clearTimeout(this.loadingTimeout);
    this.loadingTimeout = setTimeout(() => {
      DEBUG_EDITOR && console.log("QuillEditor: hasLoaded=true");
      this.hasLoaded = true;
    }, 100);
  }

  componentDidMount() {
    this._delayMarkLoaded();
  }

  componentWillReceiveProps(newProps) {
    if (
      newProps.value !== this.props.value &&
      (!this.lastValue || newProps.value !== this.lastValue)
    ) {
      DEBUG_EDITOR && console.log("QuillEditor: hasLoaded=false");
      this.hasLoaded = false;
      this._delayMarkLoaded();
    }
  }

  render() {
    let {
      onChange: _,
      value,
      basic,
      withImages,
      formats,
      inlineBasicOnly,
      tipStyle,
      ...rest
    } = this.props;

    basic = basic || inlineBasicOnly;
    formats = formats || [];

    let modules = cloneDeep(basic ? BASIC_MODULES : STANDARD_MODULES);
    if (withImages && basic) {
      modules.toolbar[0].splice(modules.toolbar[0].length - 1, 0, "image");
    }

    return (
      <div>
        <ReactQuill
          theme="snow"
          value={value || ""}
          formats={(basic ? BASIC_FORMATS : STANDARD_FORMATS).concat(formats)}
          modules={modules}
          onChange={this.onChange}
          {...rest}
        />
        {!basic && (
          <div style={Object.assign({}, STYLES.quillTip, tipStyle)}>
            Tip: Trying to paste without formatting? Use ⌘-Shift-V (Ctrl-Shift-V
            for PC) instead of the normal ⌘-V and Ctrl-V.
          </div>
        )}
      </div>
    );
  }
}

export default QuillEditor;



// WEBPACK FOOTER //
// ./src/app/components/partials/quill-editor.jsx